import { Cable, Check, Database, ListFilter, LockKeyhole, Server } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '~/elements/Button'
import { TextField } from '~/elements/Input'
import { SelectField } from '~/elements/Select'
import { useCreateConnectorMutation } from '~/features/connectors/hooks'
import type { ConnectorType } from '~/features/connectors/types'
import { cn } from '~/lib/utils'

type Step = 'connection' | 'scope' | 'rules' | 'review'

type SslMode = 'verify-full' | 'verify-ca' | 'require'

const sslModes: Array<{ value: SslMode; label: string }> = [
  { value: 'verify-full', label: 'Verify CA and hostname (recommended)' },
  { value: 'verify-ca', label: 'Verify CA only' },
  { value: 'require', label: 'Encrypt, no verification' }
]

const PEM_CERTIFICATE = /-----BEGIN CERTIFICATE-----[\s\S]+?-----END CERTIFICATE-----/

const steps: Array<{ id: Step; label: string; icon: typeof Server }> = [
  { id: 'connection', label: 'Connection', icon: Server },
  { id: 'scope', label: 'Scope', icon: Database },
  { id: 'rules', label: 'Rules', icon: ListFilter },
  { id: 'review', label: 'Review', icon: Check }
]

export function ConnectorSetupWizard({
  onClose,
  sourceType
}: {
  onClose: () => void
  sourceType: ConnectorType
}) {
  const { mutateAsync: createConnector, isPending } = useCreateConnectorMutation()
  const [step, setStep] = useState<Step>('connection')
  const [name, setName] = useState(sourceType === 'postgres' ? 'PostgreSQL source' : 'MongoDB source')
  const [host, setHost] = useState('')
  const [port, setPort] = useState(sourceType === 'postgres' ? '5432' : '27017')
  const [database, setDatabase] = useState('')
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [sslMode, setSslMode] = useState<SslMode>('verify-full')
  const [caCert, setCaCert] = useState('')
  const [entities, setEntities] = useState('')
  const [ignore, setIgnore] = useState('')
  const [error, setError] = useState<string | null>(null)

  const stepIndex = steps.findIndex((item) => item.id === step)
  const selectedEntities = useMemo(
    () =>
      entities
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [entities]
  )
  const ignorePatterns = useMemo(
    () =>
      ignore
        .split('\n')
        .map((value) => value.trim())
        .filter(Boolean),
    [ignore]
  )

  const caCertError =
    caCert.trim() && !PEM_CERTIFICATE.test(caCert) ?
      'Paste a PEM certificate (-----BEGIN CERTIFICATE-----).'
    : undefined

  const canContinue = step !== 'connection' || Boolean(host && database && !caCertError)

  const loadCaCert = async (file: File | undefined) => {
    if (file) {
      setCaCert(await file.text())
    }
  }

  const submit = async () => {
    setError(null)
    try {
      await createConnector({
        name,
        type: sourceType,
        config:
          sourceType === 'postgres' ?
            {
              host,
              port: Number(port || 5432),
              database,
              user,
              sslMode,
              ...(caCert.trim() ? { caCert: caCert.trim() } : {}),
              tables: selectedEntities,
              snapshot: true
            }
          : {
              host,
              port: Number(port || 27017),
              database,
              user,
              collections: selectedEntities,
              snapshot: true
            },
        secrets: { password },
        transform: {
          naming: 'preserve',
          mergeStrategy: 'append',
          fields: {
            ignore: ignorePatterns
          }
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create connector')
    }
  }

  const goNext = () => {
    if (stepIndex < steps.length - 1) {
      setStep(steps[stepIndex + 1].id)
    }
  }

  const goBack = () => {
    if (stepIndex > 0) {
      setStep(steps[stepIndex - 1].id)
    }
  }

  return (
    <div className="border-border mt-5 rounded-lg border bg-fill2">
      <div className="flex items-start justify-between border-b px-5 py-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Cable size={18} />
            Connect {sourceType === 'postgres' ? 'PostgreSQL' : 'MongoDB'}
          </h3>
          <p className="text-sm text-content2">Configure a paused connector, then test and resume it.</p>
        </div>
        <Button onClick={onClose} size="xsmall" variant="ghost">
          Close
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-[220px_1fr]">
        <ol className="space-y-2">
          {steps.map(({ id, label, icon: Icon }, index) => (
            <li
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                step === id ? 'bg-secondary text-content' : 'text-content2'
              )}
              key={id}
            >
              <Icon size={15} />
              <span>
                {index + 1}. {label}
              </span>
            </li>
          ))}
        </ol>

        <div className="min-h-[260px]">
          {step === 'connection' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                label="Name"
                size="small"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              <TextField
                label="Host"
                size="small"
                value={host}
                onChange={(event) => setHost(event.target.value)}
              />
              <TextField
                label="Port"
                size="small"
                value={port}
                onChange={(event) => setPort(event.target.value)}
              />
              <TextField
                label="Database"
                size="small"
                value={database}
                onChange={(event) => setDatabase(event.target.value)}
              />
              <TextField
                label="User"
                size="small"
                value={user}
                onChange={(event) => setUser(event.target.value)}
              />
              <TextField
                label="Password"
                size="small"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {sourceType === 'postgres' ?
                <>
                  <SelectField
                    caption="Connections are always encrypted."
                    className="sm:col-span-2"
                    label="TLS"
                    options={sslModes}
                    size="small"
                    value={sslMode}
                    onChange={(event) => setSslMode(event.target.value as SslMode)}
                  />
                  <div className="flex flex-col gap-1 text-sm sm:col-span-2">
                    <span className="flex items-center justify-between font-medium">
                      CA certificate (optional)
                      <label className="cursor-pointer text-xs font-normal text-content2 hover:text-content">
                        <input
                          accept=".pem,.crt,.cer"
                          className="hidden"
                          type="file"
                          onChange={(event) => loadCaCert(event.target.files?.[0])}
                        />
                        Load file
                      </label>
                    </span>
                    <textarea
                      aria-invalid={Boolean(caCertError)}
                      aria-label="CA certificate"
                      className="min-h-[96px] resize-y scrollbar-thin rounded-md border bg-secondary p-3 font-mono text-xs outline-hidden focus-visible:ring"
                      placeholder="-----BEGIN CERTIFICATE-----"
                      spellCheck={false}
                      value={caCert}
                      onChange={(event) => setCaCert(event.target.value)}
                    />
                    <span className={cn('text-xs', caCertError ? 'text-danger' : 'text-content2')}>
                      {caCertError ??
                        "Needed when the server uses a private CA (AWS RDS, Supabase, Google Cloud SQL). Paste the provider's CA bundle. Leave empty for public CAs."}
                    </span>
                  </div>
                </>
              : <p className="flex items-center gap-2 text-xs text-content2 sm:col-span-2">
                  <LockKeyhole size={14} />
                  Connections use TLS. The server must have TLS enabled.
                </p>
              }
            </div>
          )}

          {step === 'scope' && (
            <TextField
              label={sourceType === 'postgres' ? 'Tables' : 'Collections'}
              caption="Comma-separated. Leave empty to configure later."
              size="small"
              value={entities}
              onChange={(event) => setEntities(event.target.value)}
            />
          )}

          {step === 'rules' && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Ignore patterns</span>
              <textarea
                className="min-h-[190px] resize-none scrollbar-thin rounded-md border bg-secondary p-3 font-mono text-sm outline-hidden focus-visible:ring"
                placeholder={'email\npayment.**\n*.token'}
                value={ignore}
                onChange={(event) => setIgnore(event.target.value)}
              />
            </label>
          )}

          {step === 'review' && (
            <div className="grid gap-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="font-semibold">{name}</p>
                <p className="text-content2">
                  {sourceType} · {host}:{port} · {database}
                </p>
                <p className="text-content2">
                  TLS ·{' '}
                  {sourceType === 'postgres' ? describeTls(sslMode, Boolean(caCert.trim())) : 'required'}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="font-semibold">Scope</p>
                <p className="text-content2">
                  {selectedEntities.length ? selectedEntities.join(', ') : 'Configure later'}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="font-semibold">Ignored fields</p>
                <p className="text-content2">{ignorePatterns.length ? ignorePatterns.join(', ') : 'None'}</p>
              </div>
              <p className="flex items-center gap-2 text-content2">
                <LockKeyhole size={14} />
                Secrets are write-only and will not be shown after saving.
              </p>
            </div>
          )}

          {error && <p className="mt-3 text-sm text-danger">{error}</p>}
        </div>
      </div>

      <div className="flex justify-between border-t px-5 py-4">
        <Button disabled={stepIndex === 0 || isPending} onClick={goBack} size="small" variant="outline">
          Back
        </Button>
        {step === 'review' ?
          <Button disabled={isPending} loading={isPending} onClick={submit} size="small" variant="primary">
            Create connector
          </Button>
        : <Button disabled={!canContinue} onClick={goNext} size="small" variant="primary">
            Continue
          </Button>
        }
      </div>
    </div>
  )
}

// Mirrors Synx: `require` with a CA verifies the chain, like libpq.
function describeTls(mode: SslMode, customCa: boolean) {
  const effective = mode === 'require' && customCa ? 'verify-ca' : mode
  const label = sslModes.find((item) => item.value === effective)?.label ?? effective
  return customCa ? `${label} · custom CA` : label
}
